import {
  coverTitle,
  describeCover,
  displayPerson,
  formatWindow,
  worstException,
} from "@/lib/shiftCover"
import { ShiftExceptionsResult } from "@/types/collections"

export interface SheriffBookedPerson {
  key: string
  name: string | null
  window: string | null
}

export interface SheriffBooking {
  /** Never empty: a faulted route with no shift still reads "Nobody booked" rather than vanish. */
  booked: SheriffBookedPerson[]
  /** The check's own word for "the shift names someone else", not a comparison of our own. */
  differs: boolean
  /** One finding per line; null when it matched. */
  detail: string | null
}

/** Same source as the Cover panel. Null when the check said nothing (started, cancelled, past the rota horizon). */
export function sheriffBookingFor(
  routeId: string,
  shiftCover: ShiftExceptionsResult | undefined,
): SheriffBooking | null {
  if (!shiftCover) return null
  const rows = (shiftCover.exceptions ?? []).filter((e) => e.routeInstanceId === routeId)
  const covered = (shiftCover.covered ?? []).filter((c) => c.routeInstanceId === routeId)
  if (covered.length === 0 && rows.length === 0) return null

  // Every covered seat of a double-manned route; else the faulted shifts, deduplicated.
  const booked =
    covered.length > 0
      ? covered.map((c) => ({
          key: c.shiftId,
          name: displayPerson(c.shiftPersonName),
          window: formatWindow(c.shiftStart, c.shiftEnd),
        }))
      : [
          ...new Map(
            rows.filter((r) => r.shiftId).map((r) => [r.shiftId, r] as const),
          ).values(),
        ].map((r) => ({
          key: r.shiftId as string,
          name: displayPerson(r.shiftPersonName),
          window: formatWindow(r.shiftStart, r.shiftEnd),
        }))

  const worst = rows.length > 0 ? worstException(rows) : null
  return {
    booked:
      booked.length > 0
        ? booked
        : [
            {
              key: "none",
              name: displayPerson(worst?.shiftPersonName),
              window: formatWindow(worst?.shiftStart ?? null, worst?.shiftEnd ?? null),
            },
          ],
    differs: describeCover(rows).driverDiffers,
    detail: rows.length > 0 ? coverTitle(rows) : null,
  }
}
